/**
 * DEADLINE SERVICE
 *
 * Centralized deadline calculation with window validation.
 * Provides deadline-aware gatekeeping for FleetMind recommendations.
 *
 * CRITICAL: This service prevents recommending actions when deadlines have expired
 * (e.g., transfer window closed, appeal window expired).
 */

import { addDays, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import type { Ticket } from '../types';
import { inferEnforcementType } from '../utils/enforcement';
import { normalizeStage } from '../utils/stage';

/**
 * Represents a time-bound window for ticket actions
 */
export interface DeadlineWindow {
  name: string; // "Transfer Window", "Appeal Window", "Discount Period"
  deadlineDate: Date; // Absolute deadline
  daysRemaining: number; // Can be negative if expired
  hoursRemaining: number;
  minutesRemaining: number;
  isOpen: boolean; // false if expired OR stage prohibits
  isExpired: boolean; // true if deadline passed
  urgencyLevel: 'safe' | 'warning' | 'urgent' | 'expired';
  // safe: >14 days (green)
  // warning: 7-14 days (yellow)
  // urgent: 0-7 days (red)
  // expired: <0 days (gray)
  description: string; // Human-readable explanation
}

/**
 * All deadline windows for a ticket
 */
export interface TicketDeadlines {
  transferWindow: DeadlineWindow | null;
  appealWindow: DeadlineWindow | null;
  discountPeriod: DeadlineWindow | null;
  stageDeadline: DeadlineWindow | null;
  nextDeadline: DeadlineWindow | null; // The most urgent one
}

/**
 * Safely parse a date string and validate it
 */
function parseSafeDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn(`[deadlineService] Invalid date string: ${dateString}`);
    return null;
  }

  return date;
}

/**
 * Calculate urgency level based on days remaining
 */
function getUrgencyLevel(daysRemaining: number): 'safe' | 'warning' | 'urgent' | 'expired' {
  if (daysRemaining < 0) return 'expired';
  if (daysRemaining <= 7) return 'urgent';
  if (daysRemaining <= 14) return 'warning';
  return 'safe';
}

/**
 * Calculate all deadline windows for a ticket
 *
 * CRITICAL: This is the foundation of deadline-aware recommendations.
 * Transfer window ONLY available for PCN/NtO stage within 28 days.
 */
export function calculateTicketDeadlines(ticket: Ticket): TicketDeadlines {
  const now = new Date();
  const enforcementType = inferEnforcementType({
    issuing_authority: ticket.issuing_authority,
    document_type: ticket.document_type,
    document_intent: ticket.document_intent,
    key_phrases: ticket.key_phrases,
    stage: ticket.stage
  });
  const isPrivate = enforcementType === 'private';

  // Get issue date with fallback and validation
  let issueDate: Date;
  const parsedIssueDate = parseSafeDate(ticket.issue_date);
  const parsedContraventionDate = parseSafeDate(ticket.contravention_date);
  const parsedCreatedAt = parseSafeDate(ticket.created_at);

  if (parsedIssueDate) {
    issueDate = parsedIssueDate;
  } else if (parsedContraventionDate) {
    issueDate = parsedContraventionDate;
  } else if (parsedCreatedAt) {
    issueDate = parsedCreatedAt;
  } else {
    // Fallback to current time if all dates invalid
    console.warn(`[deadlineService] No valid dates found for ticket ${ticket.id}, using current time`);
    issueDate = now;
  }

  const stage = normalizeStage(ticket.stage, enforcementType);

  // ==========================================
  // TRANSFER WINDOW: ONLY for PCN/NtO within 28 days
  // ==========================================
  let transferWindow: DeadlineWindow | null = null;

  const privateTransferStages = new Set([
    'Parking Charge Notice',
    'Notice to Keeper',
    'Notice to Driver',
    'Reminder',
    'Final Reminder'
  ]);

  if (
    (!isPrivate && (stage === 'PCN Issued' || stage === 'Notice to Owner')) ||
    (isPrivate && privateTransferStages.has(stage))
  ) {
    const transferDeadline = addDays(issueDate, 28);
    const daysRemaining = differenceInDays(transferDeadline, now);
    const hoursRemaining = differenceInHours(transferDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(transferDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    transferWindow = {
      name: 'Transfer Window',
      deadlineDate: transferDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: isPrivate
        ? '28 days from issue date to transfer liability (private parking)'
        : '28 days from issue date (PCN/NtO stage only)'
    };
  }

  // ==========================================
  // APPEAL WINDOW: Varies by stage
  // ==========================================
  let appealWindow: DeadlineWindow | null = null;

  if (!isPrivate && (stage === 'PCN Issued' || stage === 'Notice to Owner')) {
    // Same as transfer window for PCN/NtO
    const appealDeadline = addDays(issueDate, 28);
    const daysRemaining = differenceInDays(appealDeadline, now);
    const hoursRemaining = differenceInHours(appealDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(appealDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    appealWindow = {
      name: 'Appeal Window',
      deadlineDate: appealDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: '28 days from issue date to make representations'
    };
  } else if (!isPrivate && stage === 'Charge Certificate') {
    // 28 days from CC date (or estimate if not provided)
    const parsedCcDate = parseSafeDate(ticket.cc_date);
    const ccDate = parsedCcDate || addDays(issueDate, 56);
    const appealDeadline = addDays(ccDate, 28);
    const daysRemaining = differenceInDays(appealDeadline, now);
    const hoursRemaining = differenceInHours(appealDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(appealDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    appealWindow = {
      name: 'Appeal Window (TPT/POPLA)',
      deadlineDate: appealDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: '28 days from Charge Certificate date to appeal to TPT/POPLA'
    };
  } else if (!isPrivate && stage === 'Order for Recovery') {
    // 21 days for statutory declaration
    const parsedOfrDate = parseSafeDate(ticket.ofr_date);
    const ofrDate = parsedOfrDate || now;
    const statDecDeadline = addDays(ofrDate, 21);
    const daysRemaining = differenceInDays(statDecDeadline, now);
    const hoursRemaining = differenceInHours(statDecDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(statDecDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    appealWindow = {
      name: 'Statutory Declaration Window',
      deadlineDate: statDecDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: '21 days from OFR to file statutory declaration'
    };
  }

  if (isPrivate) {
    const appealDeadline = addDays(issueDate, 28);
    const daysRemaining = differenceInDays(appealDeadline, now);
    const hoursRemaining = differenceInHours(appealDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(appealDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    appealWindow = {
      name: 'Appeal Window (Operator/POPLA/IAS)',
      deadlineDate: appealDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: '28 days from issue date to appeal to operator (then POPLA/IAS)'
    };
  }

  // ==========================================
  // DISCOUNT PERIOD: 14 days from issue
  // ==========================================
  let discountPeriod: DeadlineWindow | null = null;

  if (ticket.discount_amount && ticket.discount_amount > 0) {
    const discountDeadline = addDays(issueDate, 14);
    const daysRemaining = differenceInDays(discountDeadline, now);
    const hoursRemaining = differenceInHours(discountDeadline, now) % 24;
    const minutesRemaining = differenceInMinutes(discountDeadline, now) % 60;
    const isOpen = daysRemaining >= 0;

    discountPeriod = {
      name: 'Discount Period',
      deadlineDate: discountDeadline,
      daysRemaining,
      hoursRemaining,
      minutesRemaining,
      isOpen,
      isExpired: !isOpen,
      urgencyLevel: getUrgencyLevel(daysRemaining),
      description: `50% discount (£${ticket.discount_amount}) if paid within 14 days`
    };
  }

  // ==========================================
  // NEXT DEADLINE: Most urgent open window
  // ==========================================
  const allWindows = [transferWindow, appealWindow, discountPeriod].filter(Boolean) as DeadlineWindow[];
  const openWindows = allWindows.filter(w => w.isOpen);
  const nextDeadline = openWindows.length > 0
    ? openWindows.sort((a, b) => a.daysRemaining - b.daysRemaining)[0]
    : null;

  return {
    transferWindow,
    appealWindow,
    discountPeriod,
    stageDeadline: appealWindow, // Alias for appeal window
    nextDeadline
  };
}

/**
 * Check if transfer liability is allowed
 *
 * CRITICAL: This prevents recommending transfer when:
 * 1. Stage is CC/OFR (escalated beyond transfer stage)
 * 2. More than 28 days since issue (window expired)
 */
export function canTransferLiability(ticket: Ticket): { allowed: boolean; reason: string } {
  const enforcementType = inferEnforcementType({
    issuing_authority: ticket.issuing_authority,
    document_type: ticket.document_type,
    document_intent: ticket.document_intent,
    key_phrases: ticket.key_phrases,
    stage: ticket.stage
  });
  const isPrivate = enforcementType === 'private';
  const stage = normalizeStage(ticket.stage, enforcementType);

  // Check stage first
  if (!isPrivate) {
    if (stage !== 'PCN Issued' && stage !== 'Notice to Owner') {
      return {
        allowed: false,
        reason: `Transfer not available at ${ticket.stage} stage. Ticket has escalated beyond PCN/NtO.`
      };
    }
  } else {
    const privateTransferStages = new Set([
      'Parking Charge Notice',
      'Notice to Keeper',
      'Notice to Driver',
      'Reminder',
      'Final Reminder'
    ]);
    if (!privateTransferStages.has(stage)) {
      return {
        allowed: false,
        reason: `Transfer not available at ${ticket.stage} stage. Ticket has progressed beyond early private parking stages.`
      };
    }
  }

  // Check time window
  const deadlines = calculateTicketDeadlines(ticket);

  if (!deadlines.transferWindow) {
    return {
      allowed: false,
      reason: `Transfer window not available at current stage.`
    };
  }

  if (!deadlines.transferWindow.isOpen) {
    return {
      allowed: false,
      reason: `Transfer window expired ${Math.abs(deadlines.transferWindow.daysRemaining)} days ago. Deadline was ${deadlines.transferWindow.deadlineDate.toLocaleDateString('en-GB')}.`
    };
  }

  return { allowed: true, reason: '' };
}

/**
 * Format deadline countdown for display
 */
export function formatDeadlineCountdown(window: DeadlineWindow): string {
  if (window.isExpired) {
    return `Expired ${Math.abs(window.daysRemaining)} days ago`;
  }

  if (window.daysRemaining > 1) {
    return `${window.daysRemaining} days, ${window.hoursRemaining} hours`;
  } else if (window.daysRemaining === 1) {
    return `1 day, ${window.hoursRemaining} hours`;
  } else {
    return `${window.hoursRemaining} hours, ${window.minutesRemaining} minutes`;
  }
}
